import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { payrollTaxesGateway } from '../index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { sandboxTransmissionActionsEnabled } from '../../../shared/commercialActions';
import { FeatureUnavailableNotice } from '../../../shared/components/FeatureUnavailableNotice';
import { TaxFormCode, taxFormCodeLabels, type TaxDepositResponse, type TaxLiabilityResponse } from '../types';

export function PayrollTaxesPage() {
  const [liabilities, setLiabilities] = useState<TaxLiabilityResponse[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deposits, setDeposits] = useState<TaxDepositResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formCode, setFormCode] = useState<number>(TaxFormCode.Form941);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(1);

  const loadLiabilities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await payrollTaxesGateway.listLiabilities();
      setLiabilities(list.sort((a, b) => b.calculatedAtUtc.localeCompare(a.calculatedAtUtc)));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load tax liabilities.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDeposits = useCallback(async (liabilityId: string) => {
    setError(null);
    try {
      setDeposits(await payrollTaxesGateway.listDeposits(liabilityId));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load tax deposits.'));
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadLiabilities();
  }, [loadLiabilities]);

  useEffect(() => {
    // EPN-98: deposits only exist behind the mock remittance provider — never fetched when unavailable.
    if (selectedId && sandboxTransmissionActionsEnabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void loadDeposits(selectedId);
    }
  }, [selectedId, loadDeposits]);

  async function handleCalculate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await payrollTaxesGateway.calculateLiability({
        formCode, taxYear, quarter: formCode === TaxFormCode.Form941 ? quarter : null,
      });
      await loadLiabilities();
      setSelectedId(created.id);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not calculate the tax liability.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleInitiateDeposit() {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      await payrollTaxesGateway.initiateDeposit(selectedId);
      await loadDeposits(selectedId);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not initiate the tax deposit.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleCheckStatus(id: string) {
    setBusy(true);
    setError(null);
    try {
      await payrollTaxesGateway.checkDepositStatus(id);
      if (selectedId) await loadDeposits(selectedId);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not check the deposit status.'));
    } finally {
      setBusy(false);
    }
  }

  const selected = liabilities.find((l) => l.id === selectedId) ?? null;
  const hasOpenDeposit = deposits.some((d) => d.status === 'Submitted');

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Payroll Taxes</h2>
        <span className="badge badge--caution">Shadow Tax Calculation — Not Approved for Production Payroll</span>
      </div>
      <p className="muted">
        Aggregates already-computed liabilities from approved payroll runs — see{' '}
        <code>docs/payroll-taxes.md</code>.
        {sandboxTransmissionActionsEnabled
          ? ' Deposits are a simulation against a Mock remittance provider; no real agency or bank connection exists.'
          : ''}
      </p>

      {error && <p className="error">{error}</p>}

      <form className="inline-form" onSubmit={handleCalculate}>
        <select value={formCode} onChange={(e) => setFormCode(Number(e.target.value))}>
          <option value={TaxFormCode.Form941}>{taxFormCodeLabels[TaxFormCode.Form941]}</option>
          <option value={TaxFormCode.Form940}>{taxFormCodeLabels[TaxFormCode.Form940]}</option>
        </select>
        <input type="number" min="2020" max="2100" value={taxYear} onChange={(e) => setTaxYear(Number(e.target.value))} required />
        {formCode === TaxFormCode.Form941 && (
          <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
            <option value={1}>Q1</option><option value={2}>Q2</option><option value={3}>Q3</option><option value={4}>Q4</option>
          </select>
        )}
        <button type="submit" disabled={busy}>{busy ? 'Calculating…' : 'Calculate liability'}</button>
      </form>

      {loading && <p className="muted">Loading…</p>}

      {!loading && (
        <table>
          <thead><tr><th>Form</th><th>Period</th><th>Amount</th><th>Wages</th><th>Employees</th><th></th></tr></thead>
          <tbody>
            {liabilities.map((l) => (
              <tr key={l.id}>
                <td>{taxFormCodeLabels[l.formCode]}</td>
                <td>{l.taxYear}{l.quarter ? ` Q${l.quarter}` : ''}</td>
                <td>{l.totalAmount.toFixed(2)}</td>
                <td>{l.totalWages.toFixed(2)}</td>
                <td>{l.numberOfEmployees}</td>
                <td>
                  {sandboxTransmissionActionsEnabled && (
                    <button type="button" className="link" onClick={() => setSelectedId(l.id)}>
                      {selectedId === l.id ? 'Selected' : 'View deposits'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {liabilities.length === 0 && <tr><td colSpan={6} className="muted">No tax liabilities calculated yet.</td></tr>}
          </tbody>
        </table>
      )}

      {!loading && !sandboxTransmissionActionsEnabled && (
        <div className="section">
          <FeatureUnavailableNotice feature="Tax deposits (remittance)">
            EPN is not connected to a live tax-remittance provider in this release, so tax deposits cannot be
            initiated or tracked here. No deposit has been made and no funds have been moved by EPN.
          </FeatureUnavailableNotice>
        </div>
      )}

      {selected && sandboxTransmissionActionsEnabled && (
        <div className="section">
          <div className="section-header">
            <h3>Deposits — {taxFormCodeLabels[selected.formCode]} {selected.taxYear}{selected.quarter ? ` Q${selected.quarter}` : ''}</h3>
            <button type="button" disabled={busy || hasOpenDeposit} onClick={() => void handleInitiateDeposit()}>
              {hasOpenDeposit ? 'Deposit in progress' : busy ? 'Working…' : 'Initiate deposit'}
            </button>
          </div>

          <table>
            <thead><tr><th>Status</th><th>Initiated</th><th>Settled</th><th></th></tr></thead>
            <tbody>
              {deposits.map((d) => (
                <tr key={d.id}>
                  <td>
                    {d.status}
                    {d.status === 'Failed' && d.failureReason && <div className="muted">Reason: {d.failureReason}</div>}
                  </td>
                  <td>{new Date(d.initiatedAtUtc).toLocaleString()}</td>
                  <td>{d.settledAtUtc ? new Date(d.settledAtUtc).toLocaleString() : '—'}</td>
                  <td>
                    {d.status === 'Submitted' && (
                      <button type="button" className="link" disabled={busy} onClick={() => void handleCheckStatus(d.id)}>Check status</button>
                    )}
                  </td>
                </tr>
              ))}
              {deposits.length === 0 && <tr><td colSpan={4} className="muted">No deposits initiated yet for this liability.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
